import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { db, getPreference, setPreference } from "../db";
import {
  cases as casesTable,
  patients as patientsTable,
  hospitals as hospitalsTable,
  units as unitsTable,
} from "../../../shared/src/schema";
import { eq } from "drizzle-orm";
import {
  useStore,
  useCommand,
  useRpc,
  useQuery,
  useListVersion,
} from "../store";
import { useAuth } from "../auth";
import { useI18n } from "../i18n";
import { uuidv7 } from "../../../shared/src/ids";
import { useStyles, useColors, getTriageColor } from "../ui";
import { useAnon } from "../anon";
import { formatDateTime } from "../utils";
import { Pagination } from "../components/Pagination";
import { BioSpinner } from "../components/BioSpinner";
import {
  TRAUMA_TYPE_VALUES,
} from "../../../shared/src/enums";
import type {
  CaseListItem,
  CaseListResult,
} from "../../../shared/src/protocol";
import { IconTick } from "../icons";
import { batchExportPdf } from "../exportPdf";
import { FORM_FIELDS } from "./case/formFields";
import { form001, importCase } from "../importPdf";
import { renderPdfPages, extractCaseFields, isForm001, PDF_RENDER_SCALE, PDF_PAGE_H, PDF_PAGE_W, type RawPdfPages } from "../importPdf/utils";
import { BODY_INJURY_MARKERS, BURN_ZONE_MARKERS } from "../importPdf/bodyInjuries";

// ─── Column definitions ───────────────────────────────────────────────

interface ColumnDef {
  key: string;
  labelKey: string;
  defaultVisible: boolean;
  sortable: boolean;
  width?: string;
  render: (
    row: CaseListItem,
    t: (k: string) => string,
    colors: ReturnType<typeof useColors>,
  ) => React.ReactNode;
}

const COLUMNS: Record<string, ColumnDef> = {
  number: {
    key: "number",
    labelKey: "case.number",
    defaultVisible: true,
    sortable: true,
    width: "80px",
    render: (row) => row.number || "\u2014",
  },
  patientName: {
    key: "patientName",
    labelKey: "patient.name",
    defaultVisible: true,
    sortable: true,
    render: (row) => row.patientName,
  },
  dateOfBirth: {
    key: "dateOfBirth",
    labelKey: "patient.dob",
    defaultVisible: true,
    sortable: true,
    render: (row) => {
      const d = row.patientDateOfBirth;
      if (!d) return "";
      const m = d.match(/^(\d{4})-(\d{2})-(\d{2})$/);
      return m ? `${m[3]}.${m[2]}.${m[1]}` : d;
    },
  },
  patientIdNumber: {
    key: "patientIdNumber",
    labelKey: "patient.idNumber",
    defaultVisible: false,
    sortable: true,
    render: (row) => row.patientIdNumber || "",
  },
  patientSex: {
    key: "patientSex",
    labelKey: "patient.sex",
    defaultVisible: false,
    sortable: true,
    width: "80px",
    render: (row, t) => (row.patientSex ? t(`sex.${row.patientSex}`) : ""),
  },
  patientBloodType: {
    key: "patientBloodType",
    labelKey: "patient.bloodType",
    defaultVisible: false,
    sortable: true,
    width: "100px",
    render: (row, t) => {
      const bt = row.patientBloodType ? t(`blood.${row.patientBloodType}`) : "";
      const rh = row.patientRhFactor === "positive" ? "+" : row.patientRhFactor === "negative" ? "−" : "";
      return bt ? `${bt}${rh}` : rh;
    },
  },
  patientAllergyStatus: {
    key: "patientAllergyStatus",
    labelKey: "patient.allergies",
    defaultVisible: false,
    sortable: false,
    render: (row, t) => {
      if (!row.patientAllergyStatus) return "";
      const label =
        row.patientAllergyStatus === "yes"
          ? t("common.yes")
          : row.patientAllergyStatus === "no"
            ? t("common.no")
            : t("sex.unknown");
      return row.patientAllergyDetails
        ? `${label}: ${row.patientAllergyDetails}`
        : label;
    },
  },
  patientMilitaryRank: {
    key: "patientMilitaryRank",
    labelKey: "patient.rank",
    defaultVisible: false,
    sortable: true,
    render: (row) => row.patientMilitaryRank || "",
  },
  triageCategory: {
    key: "triageCategory",
    labelKey: "case.triage",
    defaultVisible: true,
    sortable: true,
    render: (row, _t, colors) =>
      row.triageCategory ? (
        <span
          style={{
            display: "inline-block",
            padding: "2px 8px",
            background:
              getTriageColor(colors)[row.triageCategory] || colors.textMuted,
            color: "#fff",
            fontSize: "0.875rem",
            fontWeight: 600,
          }}
        >
          {row.triageCategory}
        </span>
      ) : null,
  },
  traumaTypes: {
    key: "traumaTypes",
    labelKey: "case.trauma",
    defaultVisible: true,
    sortable: false,
    render: (row, t) =>
      (row.traumaTypes || []).map((tt) => t(`trauma.${tt}`)).join(", "),
  },
  eventTime: {
    key: "eventTime",
    labelKey: "case.eventTime",
    defaultVisible: true,
    sortable: true,
    render: (row) => formatDateTime(row.eventTime),
  },
  hospitalName: {
    key: "hospitalName",
    labelKey: "case.hospital",
    defaultVisible: true,
    sortable: true,
    render: (row) => row.hospitalName,
  },
  eventLocation: {
    key: "eventLocation",
    labelKey: "case.eventLocation",
    defaultVisible: false,
    sortable: true,
    render: (row) => row.eventLocation || "",
  },
  arrivalTime: {
    key: "arrivalTime",
    labelKey: "case.arrivalTime",
    defaultVisible: false,
    sortable: true,
    render: (row) => formatDateTime(row.arrivalTime),
  },
  role: {
    key: "role",
    labelKey: "case.role",
    defaultVisible: false,
    sortable: false,
    render: (row) => row.role || "",
  },
  medicalFacility: {
    key: "medicalFacility",
    labelKey: "case.medicalFacility",
    defaultVisible: false,
    sortable: false,
    render: (row) => row.medicalFacility || "",
  },
  transportType: {
    key: "transportType",
    labelKey: "case.transport",
    defaultVisible: false,
    sortable: false,
    render: (row, t) =>
      (row.transportType as string[] | null)?.map((v) => t(`transport.${v}`)).join(", ") ?? "",
  },
  traumaPenetrating: {
    key: "traumaPenetrating",
    labelKey: "case.penetrating",
    defaultVisible: false,
    sortable: false,
    render: (row) => (row.traumaPenetrating ? IconTick : ""),
  },
  conditionTypes: {
    key: "conditionTypes",
    labelKey: "case.conditions",
    defaultVisible: false,
    sortable: false,
    render: (row, t) =>
      (row.conditionTypes || []).map((ct) => t(`condition.${ct}`)).join(", "),
  },
  cbrn: {
    key: "cbrn",
    labelKey: "case.cbrn",
    defaultVisible: false,
    sortable: false,
    render: (row, t) => (row.cbrn || []).map((c) => t(`cbrn.${c}`)).join(", "),
  },
  deathTime: {
    key: "deathTime",
    labelKey: "case.deathTime",
    defaultVisible: false,
    sortable: true,
    render: (row) => formatDateTime(row.deathTime),
  },
  dischargeTime: {
    key: "dischargeTime",
    labelKey: "discharge.time",
    defaultVisible: false,
    sortable: true,
    render: (row) => formatDateTime(row.dischargeTime),
  },
  dischargeDestination: {
    key: "dischargeDestination",
    labelKey: "discharge.destination",
    defaultVisible: false,
    sortable: false,
    render: (row) => row.dischargeDestination || "",
  },
  evacuationCategory: {
    key: "evacuationCategory",
    labelKey: "discharge.evacCategory",
    defaultVisible: false,
    sortable: false,
    render: (row) => row.evacuationCategory || "",
  },
  dischargeTransportType: {
    key: "dischargeTransportType",
    labelKey: "discharge.transport",
    defaultVisible: false,
    sortable: false,
    render: (row, t) =>
      (row.dischargeTransportType as string[] | null)?.map((v) => t(`transport.${v}`)).join(", ") ?? "",
  },
  dischargePosition: {
    key: "dischargePosition",
    labelKey: "discharge.position",
    defaultVisible: false,
    sortable: false,
    render: (row, t) =>
      row.dischargePosition ? t(`position.${row.dischargePosition}`) : "",
  },
  escortDoctor: {
    key: "escortDoctor",
    labelKey: "discharge.escortDoctor",
    defaultVisible: false,
    sortable: false,
    render: (row) => (row.escortDoctor ? IconTick : ""),
  },
  escortNurse: {
    key: "escortNurse",
    labelKey: "discharge.escortNurse",
    defaultVisible: false,
    sortable: false,
    render: (row) => (row.escortNurse ? IconTick : ""),
  },
  diagnosis: {
    key: "diagnosis",
    labelKey: "case.diagnosis",
    defaultVisible: false,
    sortable: true,
    render: (row) => row.diagnosis || "",
  },
  notes: {
    key: "notes",
    labelKey: "case.notes",
    defaultVisible: false,
    sortable: false,
    render: (row) =>
      row.notes
        ? row.notes.length > 50
          ? row.notes.slice(0, 50) + "\u2026"
          : row.notes
        : "",
  },
  medicName: {
    key: "medicName",
    labelKey: "case.medicName",
    defaultVisible: false,
    sortable: true,
    render: (row) => row.medicName || "",
  },
  medicFacility: {
    key: "medicFacility",
    labelKey: "case.medicFacility",
    defaultVisible: false,
    sortable: true,
    render: (row) => row.medicFacilityName || row.medicFacility || "",
  },
  medicRole: {
    key: "medicRole",
    labelKey: "case.medicRole",
    defaultVisible: false,
    sortable: false,
    render: (row) => row.medicRole || "",
  },
  unitName: {
    key: "unitName",
    labelKey: "patient.unit",
    defaultVisible: true,
    sortable: true,
    render: (row) => row.unitName,
  },
  archived: {
    key: "archived",
    labelKey: "cases.archived",
    defaultVisible: false,
    sortable: true,
    render: (row) => (row.archived ? IconTick : ""),
  },
  updatedAt: {
    key: "updatedAt",
    labelKey: "columns.updatedAt",
    defaultVisible: true,
    sortable: true,
    render: (row) => formatDateTime(row.updatedAt),
  },
};

const COLUMN_KEYS = Object.keys(COLUMNS);

interface CaseColumnPrefs {
  visible: string[];
  sortKey: string;
  sortDir: "asc" | "desc";
}

const DEFAULT_PREFS: CaseColumnPrefs = {
  visible: COLUMN_KEYS.filter((k) => COLUMNS[k].defaultVisible),
  sortKey: "updatedAt",
  sortDir: "desc",
};

const PAGE_SIZE = 50;

// Module-level cache — survives component unmount/remount so navigating
// back doesn't flash an empty table.
let _cache: {
  cases: CaseListItem[];
  totalPages: number;
  totalCount: number;
} | null = null;

// ─── Component ────────────────────────────────────────────────────────

export function CasesPage() {
  const { t } = useI18n();
  const dispatch = useCommand();
  const { bulkDispatch, online } = useStore();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const rpc = useRpc();
  const { user } = useAuth();
  const listVersion = useListVersion();
  const styles = useStyles();
  const colors = useColors();
  const { anonName, anonUnit, anonHospital } = useAnon();

  const columnOrder = useMemo(
    () => [...COLUMN_KEYS].sort((a, b) => t(COLUMNS[a].labelKey).localeCompare(t(COLUMNS[b].labelKey))),
    [t],
  );

  // Query hospitals & units for select dropdowns
  const { data: hospitalList } = useQuery(
    async () =>
      db
        .select({ id: hospitalsTable.id, name: hospitalsTable.name })
        .from(hospitalsTable)
        .where(eq(hospitalsTable.archived, 0)),
    [],
  );

  // Read page from URL
  const currentPage = Math.max(
    1,
    parseInt(searchParams.get("page") || "1", 10) || 1,
  );

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [triageFilter, setTriageFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [traumaTypeFilter, setTraumaTypeFilter] = useState("");
  const [hospitalFilter, setHospitalFilter] = useState("");
  const [showArchived, setShowArchived] = useState(false);
  const [prefs, setPrefs] = useState<CaseColumnPrefs>(DEFAULT_PREFS);
  const [showColumnPicker, setShowColumnPicker] = useState(false);

  // ─── Selection ────────────────────────────────────────────────────────
  const [selectedIds, setSelectedIds] = useState(new Set<string>());

  const toggleId = useCallback((id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const exitSelection = useCallback(() => setSelectedIds(new Set()), []);

  const [exporting, setExporting] = useState(false);
  const [confirmingArchive, setConfirmingArchive] = useState(false);

  // ─── PDF import ───────────────────────────────────────────────────────
  const [isParsing, setIsParsing] = useState(false);
  const [importProgress, setImportProgress] = useState(0);
  const [importCaseStatus, setImportCaseStatus] = useState<{ current: number; total: number } | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const [pdfDump, setPdfDump] = useState<string | null>(null);
  const [pdfFields, setPdfFields] = useState<Record<string, string | boolean> | null>(null);
  const [dupCaseId, setDupCaseId] = useState<string | null>(null);
  const [dupCaseFields, setDupCaseFields] = useState<Record<string, string | boolean> | null>(null);
  const [pendingBatchRaw, setPendingBatchRaw] = useState<RawPdfPages | null>(null);
  const [batchSummary, setBatchSummary] = useState<{ imported: number; skipped: number } | null>(null);
  const [pdfPageImages, setPdfPageImages] = useState<string[] | null>(null);
  const [pdfDebugMode, setPdfDebugMode] = useState(false);
  const [pdfDebugCoords, setPdfDebugCoords] = useState<{ page: number; x: number; y: number } | null>(null);
  const [pdfZoom, setPdfZoom] = useState(1);
  const [hoveredMarker, setHoveredMarker] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Server data — initialise from module cache to avoid flash on re-mount
  const [cases, setCases] = useState<CaseListItem[]>(_cache?.cases ?? []);
  const [loading, setLoading] = useState(!_cache);
  const [totalPages, setTotalPages] = useState(_cache?.totalPages ?? 1);
  const [totalCount, setTotalCount] = useState(_cache?.totalCount ?? 0);
  const fetchIdRef = useRef(0);

  // Load prefs from DB
  useEffect(() => {
    getPreference<CaseColumnPrefs>("caseColumns").then((saved) => {
      if (saved) setPrefs(saved);
    });
  }, []);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(timer);
  }, [search]);

  // Save prefs to DB
  const updatePrefs = useCallback((update: Partial<CaseColumnPrefs>) => {
    setPrefs((prev) => {
      const next = { ...prev, ...update };
      setPreference("caseColumns", next);
      return next;
    });
  }, []);

  const setPage = useCallback(
    (page: number) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (page <= 1) next.delete("page");
          else next.set("page", String(page));
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // Reset to page 1 when filters change
  useEffect(() => {
    if (currentPage !== 1) setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    debouncedSearch,
    triageFilter,
    statusFilter,
    traumaTypeFilter,
    hospitalFilter,
    showArchived,
    prefs.sortKey,
    prefs.sortDir,
  ]);

  // Fetch cases from server (online) or local DB (offline)
  const fetchCases = useCallback(async () => {
    const id = ++fetchIdRef.current;
    if (!_cache) setLoading(true);
    try {
      let result: CaseListResult;
      if (online) {
        result = await rpc<CaseListResult>({
          type: "listCases",
          search: debouncedSearch || undefined,
          triage: triageFilter || undefined,
          status: statusFilter || undefined,
          traumaType: traumaTypeFilter || undefined,
          hospital: hospitalFilter || undefined,
          includeArchived: showArchived || undefined,
          sortKey: prefs.sortKey,
          sortDir: prefs.sortDir,
          page: currentPage,
          limit: PAGE_SIZE,
        });
      } else {
        // Offline: query locally synced data
        const [allCases, allPatients, allHospitals, allUnits] =
          await Promise.all([
            db.select().from(casesTable),
            db.select().from(patientsTable),
            db.select().from(hospitalsTable),
            db.select().from(unitsTable),
          ]);
        const pMap = new Map(allPatients.map((p) => [p.id, p]));
        const hMap = new Map(allHospitals.map((h) => [h.id, h]));
        const uMap = new Map(allUnits.map((u) => [u.id, u]));

        let items: CaseListItem[] = allCases.map((c) => {
          const p = pMap.get(c.patientId);
          const h = c.hospitalId ? hMap.get(c.hospitalId) : null;
          const u =
            (c.unitId ? uMap.get(c.unitId) : null) ??
            (p?.unitId ? uMap.get(p.unitId) : null);
          return {
            ...c,
            traumaTypes: c.traumaTypes ?? null,
            conditionTypes: c.conditionTypes ?? null,
            cbrn: c.cbrn ?? null,
            patientName: p?.name ?? "",
            patientDateOfBirth: p?.dateOfBirth ?? null,
            patientIdNumber: p?.idNumber ?? null,
            patientSex: p?.sex ?? null,
            patientBloodType: p?.bloodType ?? null,
            patientRhFactor: p?.rhFactor ?? null,
            patientAllergyStatus: p?.allergyStatus ?? null,
            patientAllergyDetails: p?.allergyDetails ?? null,
            patientMilitaryRank: p?.militaryRank ?? null,
            hospitalName: h?.name ?? "",
            unitName: u?.name ?? "",
            medicFacilityName: c.medicFacility ? uMap.get(c.medicFacility)?.name || c.medicFacility : "",
          } as CaseListItem;
        });

        // Apply filters
        if (!showArchived) items = items.filter((c) => !c.archived);
        if (triageFilter)
          items = items.filter((c) => c.triageCategory === triageFilter);
        if (statusFilter === "alive") items = items.filter((c) => !c.deathTime);
        if (statusFilter === "deceased")
          items = items.filter((c) => !!c.deathTime);
        if (traumaTypeFilter)
          items = items.filter((c) =>
            c.traumaTypes?.includes(traumaTypeFilter),
          );
        if (hospitalFilter)
          items = items.filter((c) => c.hospitalId === hospitalFilter);
        if (debouncedSearch) {
          const q = debouncedSearch.toLowerCase();
          items = items.filter(
            (c) =>
              c.patientName?.toLowerCase().includes(q) ||
              c.number?.toLowerCase().includes(q) ||
              c.patientIdNumber?.toLowerCase().includes(q),
          );
        }

        // Sort
        const sk = prefs.sortKey as keyof CaseListItem;
        const dir = prefs.sortDir === "asc" ? 1 : -1;
        items.sort((a, b) => {
          const av = a[sk],
            bv = b[sk];
          if (av == null && bv == null) return 0;
          if (av == null) return 1;
          if (bv == null) return -1;
          if (typeof av === "string")
            return dir * av.localeCompare(bv as string);
          if (typeof av === "number")
            return dir * ((av as number) - (bv as number));
          return 0;
        });

        // Paginate
        const total = items.length;
        const start = (currentPage - 1) * PAGE_SIZE;
        result = {
          cases: items.slice(start, start + PAGE_SIZE),
          page: currentPage,
          totalPages: Math.max(1, Math.ceil(total / PAGE_SIZE)),
          totalCount: total,
        };
      }
      if (id !== fetchIdRef.current) return; // stale
      setCases(result.cases);
      setTotalPages(result.totalPages);
      setTotalCount(result.totalCount);
      _cache = {
        cases: result.cases,
        totalPages: result.totalPages,
        totalCount: result.totalCount,
      };
    } catch (e) {
      console.error("Cases fetch error:", e);
    } finally {
      if (id === fetchIdRef.current) setLoading(false);
    }
  }, [
    online,
    rpc,
    debouncedSearch,
    triageFilter,
    statusFilter,
    traumaTypeFilter,
    hospitalFilter,
    showArchived,
    prefs.sortKey,
    prefs.sortDir,
    currentPage,
  ]);

  // Fetch on filter/sort/page change
  useEffect(() => {
    fetchCases();
  }, [fetchCases]);

  // Re-fetch when browser tab regains focus (WS may have reconnected)
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") fetchCases();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, [fetchCases]);

  // Re-fetch when server broadcasts a list invalidation
  useEffect(() => {
    if (listVersion > 0) fetchCases();
  }, [listVersion]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleSort = useCallback(
    (key: string) => {
      if (!COLUMNS[key]?.sortable) return;
      setPrefs((prev) => {
        if (prev.sortKey === key) {
          const next: typeof prev = {
            ...prev,
            sortDir: prev.sortDir === "asc" ? "desc" : "asc",
          };
          setPreference("caseColumns", next);
          return next;
        } else {
          const next = { ...prev, sortKey: key, sortDir: "desc" as const };
          setPreference("caseColumns", next);
          return next;
        }
      });
    },
    [],
  );

  const toggleColumn = (key: string) => {
    const visible = prefs.visible.includes(key)
      ? prefs.visible.filter((k) => k !== key)
      : [...prefs.visible, key];
    updatePrefs({ visible });
  };

  const createCase = async () => {
    const patientUuid = uuidv7();
    const caseUuid = uuidv7();
    const now = Date.now();

    // Prefill patient with user's unit (if any)
    const unitUuid = user?.unitIds?.[0] || "";
    await dispatch("patient.create", { uuid: patientUuid, unitUuid });

    // Create case and link patient
    await dispatch("case.create", { uuid: caseUuid });
    await dispatch("case.setPatient", { uuid: caseUuid, patientUuid });

    // Prefill event time to now
    await dispatch("case.setEventTime", { uuid: caseUuid, timestamp: now });

    // Prefill hospital from user's assignment
    const hospitalId = user?.hospitalIds?.[0];
    if (hospitalId) {
      await dispatch("case.setHospital", {
        uuid: caseUuid,
        hospitalUuid: hospitalId,
      });
    }

    // Prefill medic info for medics
    if (user && (user.role === "medicRole1" || user.role === "medicRole2")) {
      const hospitalName = hospitalId
        ? hospitalList?.find((h) => h.id === hospitalId)?.name || ""
        : "";
      await dispatch("case.setMedicName", {
        uuid: caseUuid,
        name: user.name || "",
      });
      await dispatch("case.setMedicFacility", {
        uuid: caseUuid,
        facility: hospitalName,
      });
    }

    navigate(`/cases/${caseUuid}`);
  };

  const importOptions = {
    unitUuid: user?.unitIds?.[0] || "",
    hospitalUuid: user?.hospitalIds?.[0],
  };

  const importOneCase = async (fields: Record<string, string | boolean>, { force = false } = {}) => {
    const [allPatients, allCases] = force ? [[], []] : await Promise.all([
      db.select({ id: patientsTable.id, name: patientsTable.name, dateOfBirth: patientsTable.dateOfBirth, bloodType: patientsTable.bloodType }).from(patientsTable),
      db.select({ id: casesTable.id, patientId: casesTable.patientId, eventTime: casesTable.eventTime }).from(casesTable),
    ]);
    return importCase(fields, form001, allPatients, allCases, importOptions, bulkDispatch, rpc, uuidv7);
  };

  const parsePdf = async (file: File) => {
    setIsParsing(true);
    setImportProgress(0);

    const raw = await renderPdfPages(file);

    if (raw.numPages % 2 !== 0) {
      setIsParsing(false);
      alert(t("cases.pdfPageError"));
      return;
    }

    if (!isForm001(raw.pageItems, 0)) {
      setIsParsing(false);
      alert(t("cases.pdfFormatError"));
      return;
    }

    if (raw.numPages === 2) {
      const extracted = extractCaseFields(raw.pageCanvases, raw.pageItems, 0, form001);
      if (import.meta.env.DEV) {
        // Local dev only — show dump modal with text dump and preview panel
        setPdfFields(extracted);
        setPdfPageImages(raw.pageCanvases.map((c) => c.toDataURL()));
        setPdfDump(raw.lines.join("\n"));
        setIsParsing(false);
      } else {
        setIsParsing(false);
        const result = await importOneCase(extracted);
        if (result.skipped) {
          setDupCaseId(result.caseUuid);
          setDupCaseFields(extracted);
        } else {
          navigate(`/cases/${result.caseUuid}`);
        }
      }
      return;
    }

    // Multi-case
    if (import.meta.env.DEV) {
      // Dev: pause and ask whether to import or skip duplicates
      setPendingBatchRaw(raw);
      setIsParsing(false);
    } else {
      await runBatchImport(raw, false);
    }
  };

  const runBatchImport = async (raw: RawPdfPages, force: boolean) => {
    setIsParsing(true);
    const total = raw.numPages / 2;
    const portionPerCase = 100 / total;
    setImportCaseStatus({ current: 1, total });

    const [allPatients, allCases] = force ? [[], []] : await Promise.all([
      db.select({ id: patientsTable.id, name: patientsTable.name, dateOfBirth: patientsTable.dateOfBirth, bloodType: patientsTable.bloodType }).from(patientsTable),
      db.select({ id: casesTable.id, patientId: casesTable.patientId, eventTime: casesTable.eventTime }).from(casesTable),
    ]);

    let imported = 0, skipped = 0;
    for (let i = 0; i < total; i++) {
      setImportCaseStatus({ current: i + 1, total });
      const caseStart = (i / total) * 100;
      const caseEnd = ((i + 1) / total) * 100;
      setImportProgress(caseStart);

      // Tick toward end of this case's portion at ~5s rate
      let ticked = caseStart;
      const tickStep = portionPerCase / (5000 / 400);
      const ticker = setInterval(() => {
        ticked = Math.min(caseEnd - 1, ticked + tickStep);
        setImportProgress(ticked);
      }, 400);

      const fields = extractCaseFields(raw.pageCanvases, raw.pageItems, i * 2, form001);
      const result = await importCase(fields, form001, allPatients, allCases, importOptions, bulkDispatch, rpc, uuidv7);
      if (result.skipped) skipped++; else imported++;

      clearInterval(ticker);
      setImportProgress(caseEnd);
    }

    setImportCaseStatus(null);
    setImportProgress(100);
    setIsParsing(false);
    if (skipped > 0) setBatchSummary({ imported, skipped });
  };

  const importFromPdf = async () => {
    if (!pdfFields) return;
    setIsParsing(true);
    setPdfDump(null);
    setPdfFields(null);
    setPdfPageImages(null);
    setPdfDebugMode(false);
    setPdfDebugCoords(null);
    setPdfZoom(1);

    const capturedFields = pdfFields;
    const result = await importOneCase(capturedFields);

    setIsParsing(false);
    if (result.skipped) {
      setDupCaseId(result.caseUuid);
      setDupCaseFields(capturedFields);
    } else {
      navigate(`/cases/${result.caseUuid}`);
    }
  };

  const importAnyway = async () => {
    if (!dupCaseFields) return;
    const fields = dupCaseFields;
    setDupCaseId(null);
    setDupCaseFields(null);
    setIsParsing(true);
    const result = await importOneCase(fields, { force: true });
    setIsParsing(false);
    navigate(`/cases/${result.caseUuid}`);
  };

  // Pick up a PDF shared from another Android app via the native bridge
  useEffect(() => {
    const bridge = (window as unknown as Record<string, unknown>).DataLab as { getSharedPdf?: () => string | null } | undefined;
    const b64 = bridge?.getSharedPdf?.();
    if (!b64) return;
    const byteStr = atob(b64.replace(/\s/g, ""));
    const bytes = new Uint8Array(byteStr.length);
    for (let i = 0; i < byteStr.length; i++) bytes[i] = byteStr.charCodeAt(i);
    parsePdf(new File([bytes], "shared.pdf", { type: "application/pdf" }));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Drag-and-drop column reordering (mouse + touch)
  // Tap / click → sort.  Drag (move past threshold) → reorder columns.
  const DRAG_THRESHOLD = 8; // px
  const [dragCol, setDragCol] = useState<string | null>(null);
  const [dragOverCol, setDragOverCol] = useState<string | null>(null);
  const dragCommittedRef = useRef(false);
  const touchStateRef = useRef<{
    key: string;
    startX: number;
    startY: number;
  } | null>(null);
  const touchHandledRef = useRef(false); // Prevent click after touch tap

  // Mouse: only allow native drag after the pointer moved enough
  const mouseDownRef = useRef<{ key: string; x: number; y: number } | null>(
    null,
  );

  const handleMouseDown = useCallback((key: string, e: React.MouseEvent) => {
    mouseDownRef.current = { key, x: e.clientX, y: e.clientY };
    dragCommittedRef.current = false;
  }, []);

  const handleDragStart = useCallback((key: string, e: React.DragEvent) => {
    const md = mouseDownRef.current;
    if (md) {
      const dx = e.clientX - md.x;
      const dy = e.clientY - md.y;
      if (Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD) {
        e.preventDefault(); // cancel drag — this was a click
        return;
      }
    }
    dragCommittedRef.current = true;
    setDragCol(key);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", key);
  }, []);

  const handleMouseUp = useCallback((_key: string) => {
    mouseDownRef.current = null;
  }, []);

  const handleClick = useCallback(
    (key: string) => {
      // Skip if touch already handled this tap (prevents double-toggle on mobile)
      if (touchHandledRef.current) {
        touchHandledRef.current = false;
        return;
      }
      if (!dragCommittedRef.current) {
        toggleSort(key);
      }
    },
    [toggleSort],
  );

  const handleDragOver = useCallback(
    (key: string, e: React.DragEvent) => {
      e.preventDefault();
      e.dataTransfer.dropEffect = "move";
      if (key !== dragOverCol) setDragOverCol(key);
    },
    [dragOverCol],
  );

  const handleDrop = useCallback(
    (targetKey: string, e: React.DragEvent) => {
      e.preventDefault();
      const sourceKey = dragCol;
      setDragCol(null);
      setDragOverCol(null);
      if (!sourceKey || sourceKey === targetKey) return;
      const visible = [...prefs.visible];
      const fromIdx = visible.indexOf(sourceKey);
      const toIdx = visible.indexOf(targetKey);
      if (fromIdx === -1 || toIdx === -1) return;
      visible.splice(fromIdx, 1);
      visible.splice(toIdx, 0, sourceKey);
      updatePrefs({ visible });
    },
    [dragCol, prefs.visible, updatePrefs],
  );

  const handleDragEnd = useCallback(() => {
    mouseDownRef.current = null;
    setDragCol(null);
    setDragOverCol(null);
  }, []);

  // Touch handlers for tablet/mobile column reordering.
  // All touch listeners must be native (not React) with { passive: false }
  // so we can preventDefault to stop scrolling during drag.
  const dragOverColRef = useRef<string | null>(null);
  const theadRef = useRef<HTMLTableSectionElement>(null);

  // Attach native touchstart on <thead> once, detect which <th> was touched.
  // Tap (no significant movement) → sort.  Drag past threshold → reorder.
  useEffect(() => {
    const thead = theadRef.current;
    if (!thead) return;

    const onTouchStart = (e: TouchEvent) => {
      const th = (e.target as HTMLElement).closest(
        "th[data-col-key]",
      ) as HTMLElement | null;
      if (!th) return;
      const key = th.dataset.colKey!;
      const startX = e.touches[0].clientX;
      const startY = e.touches[0].clientY;

      touchStateRef.current = { key, startX, startY };
      dragCommittedRef.current = false;
      dragOverColRef.current = null;

      const onTouchMove = (ev: TouchEvent) => {
        const ts = touchStateRef.current;
        if (!ts) return;
        const dx = ev.touches[0].clientX - ts.startX;
        const dy = ev.touches[0].clientY - ts.startY;
        if (!dragCommittedRef.current) {
          if (Math.abs(dx) < DRAG_THRESHOLD && Math.abs(dy) < DRAG_THRESHOLD)
            return;
          // Commit to drag
          dragCommittedRef.current = true;
          e.preventDefault(); // retroactively prevent default on the original touchstart
          setDragCol(ts.key);
          setDragOverCol(null);
        }
        ev.preventDefault();
        const touch = ev.touches[0];
        const target = document.elementFromPoint(touch.clientX, touch.clientY);
        const targetTh = target?.closest(
          "th[data-col-key]",
        ) as HTMLElement | null;
        const targetKey = targetTh?.dataset.colKey || null;
        if (targetKey && targetKey !== dragOverColRef.current) {
          dragOverColRef.current = targetKey;
          setDragOverCol(targetKey);
        }
      };

      const onTouchEnd = () => {
        document.removeEventListener("touchmove", onTouchMove);
        document.removeEventListener("touchend", onTouchEnd);
        document.removeEventListener("touchcancel", onTouchEnd);
        const sourceKey = touchStateRef.current?.key;
        touchStateRef.current = null;

        if (!dragCommittedRef.current) {
          // Was a tap — trigger sort
          setDragCol(null);
          setDragOverCol(null);
          if (sourceKey) {
            touchHandledRef.current = true; // Prevent synthetic click from double-toggling
            toggleSort(sourceKey);
          }
          return;
        }

        const targetKey = dragOverColRef.current;
        dragOverColRef.current = null;
        setDragCol(null);
        setDragOverCol(null);
        if (!sourceKey || !targetKey || sourceKey === targetKey) return;
        setPrefs((prev) => {
          const visible = [...prev.visible];
          const fromIdx = visible.indexOf(sourceKey);
          const toIdx = visible.indexOf(targetKey);
          if (fromIdx === -1 || toIdx === -1) return prev;
          visible.splice(fromIdx, 1);
          visible.splice(toIdx, 0, sourceKey);
          const next = { ...prev, visible };
          setPreference("caseColumns", next);
          return next;
        });
      };

      document.addEventListener("touchmove", onTouchMove, { passive: false });
      document.addEventListener("touchend", onTouchEnd);
      document.addEventListener("touchcancel", onTouchEnd);
    };

    thead.addEventListener("touchstart", onTouchStart, { passive: false });
    return () => thead.removeEventListener("touchstart", onTouchStart);
  }, [toggleSort]);

  const visibleCols = prefs.visible.map((k) => COLUMNS[k]).filter(Boolean);

  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        overflow: "hidden",
        position: "relative",
      }}
      onDragEnter={(e) => {
        if (e.dataTransfer.types.includes("Files")) {
          e.preventDefault();
          setDragOver(true);
        }
      }}
      onDragOver={(e) => {
        if (e.dataTransfer.types.includes("Files")) {
          e.preventDefault();
          setDragOver(true);
        }
      }}
      onDragLeave={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node)) {
          setDragOver(false);
        }
      }}
      onDrop={(e) => {
        e.preventDefault();
        setDragOver(false);
        const file = e.dataTransfer.files[0];
        if (file) parsePdf(file);
      }}
    >
      {/* Drag-over overlay */}
      {dragOver && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            background: "rgba(0,0,0,0.5)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            pointerEvents: "none",
          }}
        >
          <div
            style={{
              background: "#fff",
              borderRadius: 12,
              padding: "32px 48px",
              fontSize: "1.5rem",
              fontWeight: 600,
              boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
            }}
          >
            {t("cases.dropPdf")}
          </div>
        </div>
      )}

      {/* PDF parsing/importing spinner overlay */}
      {isParsing && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            background: "rgba(0,0,0,0.7)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 20,
            pointerEvents: "none",
          }}
        >
          <BioSpinner size={80} />
          {importCaseStatus !== null ? (
            <div style={{ color: "#fff", fontSize: "0.85rem", fontWeight: 600, letterSpacing: "0.08em" }}>
              {t("cases.pdfCaseProgress", {
                current: String(importCaseStatus.current),
                total: String(importCaseStatus.total),
              })}
            </div>
          ) : null}
          {importCaseStatus !== null ? (
            <div style={{ width: 200, height: 4, background: "rgba(255,255,255,0.2)", borderRadius: 2, overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${importProgress}%`, background: "rgba(255,255,255,0.85)", borderRadius: 2, transition: "width 0.08s linear" }} />
            </div>
          ) : null}
        </div>
      )}

      {/* PDF text dump modal */}
      {pdfDump !== null && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 9999,
            background: "rgba(0,0,0,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <div
            style={{
              background: colors.bg,
              borderRadius: 8,
              padding: 24,
              width: "90vw",
              maxWidth: 900,
              maxHeight: "85vh",
              display: "flex",
              flexDirection: "column",
              gap: 12,
              boxShadow: "0 8px 32px rgba(0,0,0,0.3)",
            }}
          >
            <div
              style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <strong style={{ fontSize: "1rem" }}>{t("cases.pdfDump")}</strong>
              <div style={{ display: "flex", gap: 8 }}>
                {pdfFields && Object.values(pdfFields).some((v) => typeof v === "string" && v) && (
                  <button onClick={importFromPdf} style={styles.btnPrimary}>
                    {t("cases.importToCase")}
                  </button>
                )}
                <button
                  onClick={() => { setPdfDebugMode((m) => !m); setPdfZoom(1); }}
                  style={pdfDebugMode ? styles.btnPrimary : styles.btn}
                >
                  Preview
                </button>
                <button onClick={() => { setPdfDump(null); setPdfFields(null); setPdfPageImages(null); setPdfDebugMode(false); setPdfDebugCoords(null); setPdfZoom(1); }} style={styles.btn}>
                  {t("cases.pdfDumpClose")}
                </button>
              </div>
            </div>
            {pdfDebugMode && pdfPageImages && (
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                {pdfDebugCoords && (
                  <div style={{ fontFamily: "monospace", fontSize: "0.9rem", padding: "6px 10px", background: colors.bgAlt, border: `1px solid ${colors.border}`, borderRadius: 4 }}>
                    Page {pdfDebugCoords.page} — x:{pdfDebugCoords.x} y:{pdfDebugCoords.y}
                  </div>
                )}
                <div style={{ display: "flex", alignItems: "center", gap: 4, marginLeft: "auto" }}>
                  <button onClick={() => setPdfZoom((z) => Math.max(1, z - 0.5))} style={styles.btn}>−</button>
                  <span style={{ fontFamily: "monospace", fontSize: "0.8rem", minWidth: 36, textAlign: "center" }}>{pdfZoom}×</span>
                  <button onClick={() => setPdfZoom((z) => Math.min(8, z + 0.5))} style={styles.btn}>+</button>
                </div>
              </div>
            )}
            <div style={{ flex: 1, overflow: "auto" }}>
              {pdfDebugMode && pdfPageImages ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  {pdfPageImages.map((src, i) => {
                    const pageNum = i + 1;
                    const textMarkers = form001.textFields.filter((f) => f.page === pageNum);
                    const checkMarkers = form001.checkboxFields.filter((f) => f.page === pageNum);
                    const injuryMarkers = form001.bodyInjuryPage === pageNum ? BODY_INJURY_MARKERS : [];
                    const burnMarkers = form001.bodyInjuryPage === pageNum ? BURN_ZONE_MARKERS : [];
                    return (
                      <div key={i}>
                        <div style={{ fontSize: "0.75rem", color: colors.textMuted, marginBottom: 4 }}>Page {pageNum}</div>
                        <div style={{ position: "relative", display: "inline-block", width: `${pdfZoom * 100}%` }}>
                          <img
                            src={src}
                            alt={`Page ${pageNum}`}
                            style={{ width: "100%", cursor: "crosshair", display: "block", border: `1px solid ${colors.border}` }}
                            onClick={(e) => {
                              const img = e.currentTarget;
                              const rect = img.getBoundingClientRect();
                              const relX = e.clientX - rect.left;
                              const relY = e.clientY - rect.top;
                              const canvasX = relX * (img.naturalWidth / img.clientWidth);
                              const canvasY = relY * (img.naturalHeight / img.clientHeight);
                              const pdfX = Math.round(canvasX / PDF_RENDER_SCALE);
                              const pdfY = Math.round(PDF_PAGE_H - canvasY / PDF_RENDER_SCALE);
                              setPdfDebugCoords({ page: pageNum, x: pdfX, y: pdfY });
                            }}
                          />
                          {textMarkers.map((f) => (
                            <div
                              key={f.key}
                              onMouseEnter={() => setHoveredMarker(f.key)}
                              onMouseLeave={() => setHoveredMarker(null)}
                              style={{
                                position: "absolute",
                                left: `${(f.x / PDF_PAGE_W) * 100}%`,
                                top: `${((PDF_PAGE_H - f.y) / PDF_PAGE_H) * 100}%`,
                                cursor: "default",
                              }}
                            >
                              <div style={{ width: 10, height: 10, borderRadius: "50%", background: "rgba(59,130,246,0.85)", border: "2px solid #1d4ed8", transform: "translate(-50%, -50%)", pointerEvents: "none" }} />
                              {hoveredMarker === f.key && <div style={{ fontSize: "0.6rem", color: "#1d4ed8", background: "rgba(255,255,255,0.85)", padding: "1px 3px", borderRadius: 2, whiteSpace: "nowrap", transform: "translateX(-50%)", pointerEvents: "none" }}>{f.key}</div>}
                            </div>
                          ))}
                          {checkMarkers.map((f) => (
                            <div
                              key={f.key}
                              onMouseEnter={() => setHoveredMarker(f.key)}
                              onMouseLeave={() => setHoveredMarker(null)}
                              style={{
                                position: "absolute",
                                left: `${(f.x / PDF_PAGE_W) * 100}%`,
                                top: `${((PDF_PAGE_H - f.y) / PDF_PAGE_H) * 100}%`,
                                cursor: "default",
                              }}
                            >
                              <div style={{ width: 10, height: 10, borderRadius: 2, background: "rgba(34,197,94,0.85)", border: "2px solid #15803d", transform: "translate(-50%, -50%)", pointerEvents: "none" }} />
                              {hoveredMarker === f.key && <div style={{ fontSize: "0.6rem", color: "#15803d", background: "rgba(255,255,255,0.85)", padding: "1px 3px", borderRadius: 2, whiteSpace: "nowrap", transform: "translateX(-50%)", pointerEvents: "none" }}>{f.key}</div>}
                            </div>
                          ))}
                          {burnMarkers.length > 0 && (
                            <svg
                              style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }}
                              viewBox={`0 0 ${PDF_PAGE_W} ${PDF_PAGE_H}`}
                              preserveAspectRatio="none"
                            >
                              {burnMarkers.map((b) => (
                                <polygon
                                  key={b.zoneId}
                                  points={b.points.map(([px, py]) => `${px},${PDF_PAGE_H - py}`).join(" ")}
                                  fill="rgba(239,68,68,0.25)"
                                  stroke="rgba(220,38,38,0.8)"
                                  strokeWidth="1"
                                >
                                  <title>{b.zoneId}</title>
                                </polygon>
                              ))}
                            </svg>
                          )}
                          {injuryMarkers.map((m) => (
                            <div
                              key={m.id}
                              onMouseEnter={() => setHoveredMarker(m.id)}
                              onMouseLeave={() => setHoveredMarker(null)}
                              style={{
                                position: "absolute",
                                left: `${(m.pdfX / PDF_PAGE_W) * 100}%`,
                                top: `${((PDF_PAGE_H - m.pdfY) / PDF_PAGE_H) * 100}%`,
                                // detection point — diamond shows where pixel is sampled
                                cursor: "default",
                              }}
                            >
                              <div style={{ width: m.radius * 2, height: m.radius * 2, background: "rgba(249,115,22,0.85)", border: "2px solid #c2410c", transform: "translate(-50%, -50%) rotate(45deg)", pointerEvents: "none" }} />
                              {hoveredMarker === m.id && <div style={{ fontSize: "0.6rem", color: "#c2410c", background: "rgba(255,255,255,0.85)", padding: "1px 3px", borderRadius: 2, whiteSpace: "nowrap", transform: "translateX(-50%)", pointerEvents: "none" }}>{m.id}</div>}
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <textarea
                  readOnly
                  value={pdfDump}
                  style={{
                    width: "100%",
                    height: "100%",
                    boxSizing: "border-box",
                    fontFamily: "monospace",
                    fontSize: "0.75rem",
                    whiteSpace: "pre",
                    resize: "none",
                    border: `1px solid ${colors.border}`,
                    borderRadius: 4,
                    padding: 8,
                    minHeight: 400,
                    background: colors.bgAlt,
                    color: colors.text,
                  }}
                />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Single import: duplicate case detected */}
      {dupCaseId !== null && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.25)" }}
          onClick={() => setDupCaseId(null)}>
          <div style={{ background: colors.bg, border: `1px solid ${colors.border}`, boxShadow: "0 8px 24px rgba(0,0,0,0.2)", padding: "1rem 1.25rem", borderRadius: "6px", display: "flex", flexDirection: "column", gap: "0.75rem", minWidth: "16rem", maxWidth: "24rem", textAlign: "center" }}
            onClick={(e) => e.stopPropagation()}>
            <div style={{ fontSize: "0.9rem", color: colors.text }}>{t("cases.dupCaseTitle")}</div>
            <div style={{ fontSize: "0.8rem", color: colors.textMuted }}>{t("cases.dupCaseDesc")}</div>
            <div style={{ display: "flex", gap: "0.5rem", justifyContent: "center", flexWrap: "wrap" }}>
              <button style={{ ...styles.btn, fontSize: "0.8rem", padding: "0.3rem 0.75rem" }} onClick={() => { setDupCaseId(null); setDupCaseFields(null); }}>{t("common.dismiss")}</button>
              <button style={{ ...styles.btnPrimary, fontSize: "0.8rem", padding: "0.3rem 0.75rem" }} onClick={() => { const id = dupCaseId; setDupCaseId(null); setDupCaseFields(null); navigate(`/cases/${id}`); }}>{t("cases.openExistingCase")}</button>
              {import.meta.env.DEV && <button style={{ ...styles.btn, fontSize: "0.8rem", padding: "0.3rem 0.75rem", color: colors.danger, borderColor: colors.danger }} onClick={importAnyway}>{t("cases.importAnyway")}</button>}
            </div>
          </div>
        </div>
      )}

      {/* Dev: batch duplicate handling choice */}
      {pendingBatchRaw !== null && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.25)" }}>
          <div style={{ background: colors.bg, border: `1px solid ${colors.border}`, boxShadow: "0 8px 24px rgba(0,0,0,0.2)", padding: "1rem 1.25rem", borderRadius: "6px", display: "flex", flexDirection: "column", gap: "0.75rem", minWidth: "16rem", maxWidth: "24rem", textAlign: "center" }}
            onClick={(e) => e.stopPropagation()}>
            <div style={{ fontSize: "0.9rem", color: colors.text }}>{t("cases.batchDupChoiceTitle")}</div>
            <div style={{ fontSize: "0.8rem", color: colors.textMuted }}>{t("cases.batchDupChoiceDesc", { count: String(pendingBatchRaw.numPages / 2) })}</div>
            <div style={{ display: "flex", gap: "0.5rem", justifyContent: "center" }}>
              <button style={{ ...styles.btn, fontSize: "0.8rem", padding: "0.3rem 0.75rem" }} onClick={() => { const r = pendingBatchRaw; setPendingBatchRaw(null); runBatchImport(r, false); }}>{t("cases.batchDupSkip")}</button>
              <button style={{ ...styles.btnPrimary, fontSize: "0.8rem", padding: "0.3rem 0.75rem" }} onClick={() => { const r = pendingBatchRaw; setPendingBatchRaw(null); runBatchImport(r, true); }}>{t("cases.batchDupImport")}</button>
            </div>
          </div>
        </div>
      )}

      {/* Batch import: summary with skipped count */}
      {batchSummary !== null && (
        <div style={{ position: "fixed", inset: 0, zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.25)" }}
          onClick={() => setBatchSummary(null)}>
          <div style={{ background: colors.bg, border: `1px solid ${colors.border}`, boxShadow: "0 8px 24px rgba(0,0,0,0.2)", padding: "1rem 1.25rem", borderRadius: "6px", display: "flex", flexDirection: "column", gap: "0.75rem", minWidth: "16rem", maxWidth: "24rem", textAlign: "center" }}
            onClick={(e) => e.stopPropagation()}>
            <div style={{ fontSize: "0.9rem", color: colors.text }}>{t("cases.batchSummaryTitle")}</div>
            <div style={{ fontSize: "0.8rem", color: colors.textMuted }}>{t("cases.batchSummaryDesc", { imported: String(batchSummary.imported), skipped: String(batchSummary.skipped) })}</div>
            <div style={{ display: "flex", justifyContent: "center" }}>
              <button style={{ ...styles.btnPrimary, fontSize: "0.8rem", padding: "0.3rem 0.75rem" }} onClick={() => setBatchSummary(null)}>{t("common.dismiss")}</button>
            </div>
          </div>
        </div>
      )}

      {selectedIds.size > 0 && (
        <div
          style={{
            ...styles.toolbar,
            background: colors.primary,
            borderBottom: `1px solid ${colors.border}`,
          }}
        >
          <button
            onClick={exitSelection}
            style={{
              background: "none",
              border: "none",
              color: "#fff",
              fontSize: "1.25rem",
              cursor: "pointer",
              lineHeight: 1,
              padding: "0 4px",
            }}
          >
            ✕
          </button>
          <span style={{ color: "#fff", fontWeight: 600 }}>
            {t("common.selected", { count: selectedIds.size })}
          </span>
          <span style={{ flex: 1 }} />
          <button
            style={{ ...styles.btn, background: "#fff", color: colors.primary }}
            disabled={exporting}
            onClick={async () => {
              if (exporting) return;
              setExporting(true);
              try {
                const bytes = await batchExportPdf([...selectedIds], FORM_FIELDS);
                const fileName = `cases-${new Date().toISOString().slice(0, 10)}.pdf`;
                if (window.DataLab?.shareFile) {
                  const b64 = btoa(String.fromCharCode(...new Uint8Array(bytes)));
                  window.DataLab.shareFile(b64, "application/pdf", fileName);
                } else {
                  const url = URL.createObjectURL(new Blob([bytes], { type: "application/pdf" }));
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = fileName;
                  a.click();
                  URL.revokeObjectURL(url);
                }
              } finally {
                setExporting(false);
              }
            }}
          >
            {exporting ? "..." : t("common.export")}
          </button>
          {user?.role === "admin" && (
            <button
              style={{
                ...styles.btn,
                background: "#ef4444",
                color: "#fff",
                border: "none",
              }}
              onClick={() => setConfirmingArchive(true)}
            >
              {t("case.archive")}
            </button>
          )}
        </div>
      )}
      {confirmingArchive && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 1000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(0,0,0,0.25)",
          }}
          onClick={() => setConfirmingArchive(false)}
        >
          <div
            style={{
              background: colors.bg,
              border: `1px solid ${colors.border}`,
              boxShadow: "0 8px 24px rgba(0,0,0,0.2)",
              padding: "1rem 1.25rem",
              borderRadius: "6px",
              display: "flex",
              flexDirection: "column",
              gap: "0.75rem",
              minWidth: "16rem",
              textAlign: "center",
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ fontSize: "0.9rem", color: colors.text }}>
              {t("common.archiveCasesConfirm", { count: selectedIds.size })}
            </div>
            <div style={{ display: "flex", gap: "0.5rem", justifyContent: "center" }}>
              <button
                style={{ ...styles.btn, fontSize: "0.8rem", padding: "0.3rem 0.75rem" }}
                onClick={() => setConfirmingArchive(false)}
              >
                {t("common.cancel")}
              </button>
              <button
                style={{
                  ...styles.btnPrimary,
                  fontSize: "0.8rem",
                  padding: "0.3rem 0.75rem",
                  background: colors.danger,
                }}
                onClick={async () => {
                  setConfirmingArchive(false);
                  for (const id of selectedIds) {
                    await dispatch("case.archive", { uuid: id });
                  }
                  exitSelection();
                }}
              >
                {t("case.archive")}
              </button>
            </div>
          </div>
        </div>
      )}
      <div style={{ borderBottom: `1px solid ${colors.border}` }}>
        <div style={{ ...styles.toolbar, borderBottom: "none" }}>
          <input
            placeholder={t("cases.search")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ ...styles.input, maxWidth: "240px" }}
          />
          <label
            style={{
              display: "flex",
              alignItems: "center",
              gap: "4px",
              fontSize: "0.875rem",
              color: colors.textMuted,
            }}
          >
            <input
              type="checkbox"
              checked={showArchived}
              onChange={(e) => setShowArchived(e.target.checked)}
            />
            {t("common.showArchived")}
          </label>
          <div style={{ position: "relative" }}>
            <button
              onClick={() => setShowColumnPicker(!showColumnPicker)}
              style={{
                background: "none",
                border: "none",
                padding: 0,
                cursor: "pointer",
                color: colors.primary,
                fontSize: "0.875rem",
              }}
            >
              {t("columns.configure")} {showColumnPicker ? "▲" : "▼"}
            </button>
            {showColumnPicker && (
              <div
                style={{
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  zIndex: 100,
                  background: colors.bg,
                  border: `1px solid ${colors.border}`,
                  padding: "8px",
                  minWidth: "200px",
                  maxHeight: "400px",
                  overflowY: "auto",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.15)",
                }}
              >
                <div
                  style={{
                    fontSize: "0.75rem",
                    fontWeight: 600,
                    color: colors.textMuted,
                    marginBottom: "8px",
                    textTransform: "uppercase",
                  }}
                >
                  {t("columns.visible")}
                </div>
                {columnOrder.map((key) => {
                  const col = COLUMNS[key];
                  return (
                    <label
                      key={key}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "6px",
                        padding: "4px 0",
                        cursor: "pointer",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={prefs.visible.includes(key)}
                        onChange={() => toggleColumn(key)}
                      />
                      <span
                        style={{ fontSize: "0.875rem", color: colors.text }}
                      >
                        {t(col.labelKey)}
                      </span>
                    </label>
                  );
                })}
                <div
                  style={{
                    marginTop: "8px",
                    paddingTop: "8px",
                    borderTop: `1px solid ${colors.border}`,
                  }}
                >
                  <button
                    onClick={() =>
                      updatePrefs({
                        visible: COLUMN_KEYS.filter(
                          (k) => COLUMNS[k].defaultVisible,
                        ),
                      })
                    }
                    style={{
                      ...styles.btn,
                      fontSize: "0.75rem",
                      padding: "4px 8px",
                    }}
                  >
                    {t("columns.reset")}
                  </button>
                </div>
              </div>
            )}
          </div>
          <span style={{ fontSize: "0.8rem", color: colors.textMuted }}>
            {totalCount > 0 && `${totalCount}`}
          </span>
          <span style={{ flex: 1 }} />
          <input
            type="file"
            accept=".pdf"
            ref={fileInputRef}
            style={{ display: "none" }}
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) parsePdf(f);
              e.target.value = "";
            }}
          />
          <button
            onClick={() => fileInputRef.current?.click()}
            style={styles.btn}
          >
            {t("cases.import")}
          </button>
          <button onClick={createCase} style={styles.btnPrimary}>
            {t("cases.new")}
          </button>
        </div>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "8px",
            padding: "0 12px 8px",
            flexWrap: "wrap",
          }}
        >
          <select
            value={triageFilter}
            onChange={(e) => setTriageFilter(e.target.value)}
            style={{ ...styles.select, width: "auto" }}
          >
            <option value="">{t("cases.triageFilter")}</option>
            <option value="I">I</option>
            <option value="II">II</option>
            <option value="III">III</option>
            <option value="IV">IV</option>
          </select>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{ ...styles.select, width: "auto" }}
          >
            <option value="">{t("cases.statusFilter")}</option>
            <option value="alive">{t("cases.alive")}</option>
            <option value="deceased">{t("cases.deceased")}</option>
          </select>
          <select
            value={traumaTypeFilter}
            onChange={(e) => setTraumaTypeFilter(e.target.value)}
            style={{ ...styles.select, width: "auto" }}
          >
            <option value="">{t("cases.traumaFilter")}</option>
            {TRAUMA_TYPE_VALUES.map((v) => (
              <option key={v} value={v}>
                {t(`trauma.${v}`)}
              </option>
            ))}
          </select>
          <select
            value={hospitalFilter}
            onChange={(e) => setHospitalFilter(e.target.value)}
            style={{ ...styles.select, width: "auto" }}
          >
            <option value="">{t("cases.hospitalFilter")}</option>
            {(hospitalList || []).map((h) => (
              <option key={h.id} value={h.id}>
                {anonHospital(h.id, h.name)}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div style={{ overflow: "auto", flex: 1, minWidth: 0 }}>
        <table style={styles.table}>
          <thead ref={theadRef} style={{ touchAction: "none" }}>
            <tr>
              <th
                style={{
                  ...styles.th,
                  position: "sticky",
                  left: 0,
                  zIndex: 2,
                  width: "36px",
                  padding: "4px",
                  textAlign: "center",
                }}
              />
              {visibleCols.map((col) => (
                <th
                  key={col.key}
                  data-col-key={col.key}
                  draggable
                  onMouseDown={(e) => handleMouseDown(col.key, e)}
                  onMouseUp={() => handleMouseUp(col.key)}
                  onClick={() => handleClick(col.key)}
                  onDragStart={(e) => handleDragStart(col.key, e)}
                  onDragOver={(e) => handleDragOver(col.key, e)}
                  onDrop={(e) => handleDrop(col.key, e)}
                  onDragEnd={handleDragEnd}
                  style={{
                    ...styles.th,
                    userSelect: "none",
                    cursor: col.sortable ? "pointer" : "grab",
                    ...(col.width ? { width: col.width } : {}),
                    ...(dragCol === col.key
                      ? { opacity: 0.3, background: colors.primary + "22" }
                      : {}),
                    ...(dragOverCol === col.key && dragCol !== col.key
                      ? {
                          borderLeft: `3px solid ${colors.primary}`,
                          background: colors.primary + "18",
                        }
                      : {}),
                  }}
                >
                  {t(col.labelKey)}
                  {prefs.sortKey === col.key && (
                    <span style={{ marginLeft: "4px" }}>
                      {prefs.sortDir === "asc" ? "\u25B2" : "\u25BC"}
                    </span>
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {cases.length === 0 && !loading && (
              <tr>
                <td
                  colSpan={visibleCols.length + 1}
                  style={{
                    ...styles.td,
                    color: colors.textMuted,
                    textAlign: "center",
                  }}
                >
                  {t("cases.noResults")}
                </td>
              </tr>
            )}
            {cases.map((c) => {
              const isSelected = selectedIds.has(c.id);
              return (
              <tr
                key={c.id}
                data-case-id={c.id}
                onClick={() => navigate(`/cases/${c.id}`)}
                style={{
                  cursor: "pointer",
                  background: isSelected ? colors.primary + "28" : "",
                }}
              >
                <td
                  style={{
                    ...styles.td,
                    position: "sticky",
                    left: 0,
                    zIndex: 1,
                    background: isSelected ? colors.primary + "28" : colors.bg,
                    width: "36px",
                    padding: "4px",
                    textAlign: "center",
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleId(c.id)}
                    style={{ cursor: "pointer" }}
                  />
                </td>
                {visibleCols.map((col) => (
                  <td
                    key={col.key}
                    style={{
                      ...styles.td,
                      ...(dragCol === col.key ? { opacity: 0.3 } : {}),
                      ...(dragOverCol === col.key && dragCol !== col.key
                        ? { borderLeft: `3px solid ${colors.primary}` }
                        : {}),
                    }}
                  >
                    {col.key === "patientName" ? anonName(c.patientName)
                      : col.key === "dateOfBirth" || col.key === "patientIdNumber" ? (anonName(col.render(c, t, colors) as string))
                      : col.key === "unitName" ? anonUnit(c.unitId, c.unitName)
                      : col.key === "hospitalName" ? anonHospital(c.hospitalId, c.hospitalName)
                      : col.render(c, t, colors)}
                  </td>
                ))}
              </tr>
              );
            })}
          </tbody>
        </table>

        {loading && (
          <div
            style={{
              padding: "12px",
              textAlign: "center",
              color: colors.textMuted,
            }}
          >
            {t("app.loading")}
          </div>
        )}
      </div>

      <Pagination
        currentPage={currentPage}
        totalPages={totalPages}
        onPageChange={setPage}
      />

      {/* Click outside to close column picker */}
      {showColumnPicker && (
        <div
          style={{ position: "fixed", inset: 0, zIndex: 99 }}
          onClick={() => setShowColumnPicker(false)}
        />
      )}
    </div>
  );
}
